import { useState } from "react";
import moment from "moment";
import AlertBox from "../../components/AlertBox";
import Box from "../../components/Box";
import MainLayout from "../../components/layouts/MainLayout";
import Pagination from "../../components/Pagination";
import ModalDownloadInstallationKpi from "../../components/schedule/ModalDownloadInstallationKpi";
import { useControlPermission } from "../../utils/ActionPermissionUtils";
import { useGroupListHooks } from "../../utils/ProjectsUtilHooks";
import { useInstallationScheduleKpiHooks } from "../../utils/SchedulesUtil";

function formatDate(timestamp) {
  return timestamp == null ? "-" : moment.unix(timestamp).format("DD MMM YYYY - HH:mm");
}

function InstallationStatusBadge({ status }) {
  const normalizedStatus = (status || "").trim().toLowerCase();
  const readyToInstall = ["survey-paid", "sudah survey - siap pasang"].includes(normalizedStatus);
  const unpaid = [
    "survey-unpaid",
    "sudah survey - belum bayar",
    "sudah survey - belum dibayar",
  ].includes(normalizedStatus);
  const installed = normalizedStatus === "sudah survey - sudah dipasang";
  const rejected = ["survey-uninstallable", "sudah survey - ditolak"].includes(normalizedStatus);
  const canceled = ["survey-canceled", "sudah survey - dibatalkan"].includes(normalizedStatus);

  if (readyToInstall || unpaid || installed || rejected || canceled) {
    return (
      <>
        <span className="badge badge-info">Sudah Disurvey</span>
        <br />
        {readyToInstall || installed ? (
          <span className="badge badge-success">{installed ? "Sudah Dipasang" : "Siap Pasang"}</span>
        ) : (
          <span className={`badge badge-${unpaid ? "warning" : "danger"}`}>
            {rejected ? "Ditolak" : canceled ? "Dibatalkan" : "Belum Bayar"}
          </span>
        )}
      </>
    );
  }

  if (["no-survey", "belum survey"].includes(normalizedStatus)) {
    return <span className="badge badge-warning">Belum Survey</span>;
  }

  return status || "-";
}

export default function InstallationKpiPage() {
  const { hasAccess } = useControlPermission();
  const { projects } = useGroupListHooks();
  const [formFilter, setFormFilter] = useState({
    survey_status: "",
    project_id: "",
    date_from: "",
    date_until: "",
  });
  const [filter, setFilter] = useState({
    survey_status: "",
    project_id: "",
    date_from: "",
    date_until: "",
    page: 1,
    page_size: 10,
  });
  const [showModalDownload, setShowModalDownload] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  const { data, meta, loading, errorMsg } = useInstallationScheduleKpiHooks({ filter });
  const totalPages = Math.ceil(meta.count / meta.page_size);

  const changeFilter = (name, value) => {
    setFormFilter({ ...formFilter, [name]: value });
  };

  const applyFilter = (event) => {
    event.preventDefault();
    setFilter({ ...filter, ...formFilter, page: 1 });
  };

  const resetFilter = () => {
    setFormFilter({ survey_status: "", project_id: "", date_from: "", date_until: "" });
    setFilter({
      survey_status: "",
      project_id: "",
      date_from: "",
      date_until: "",
      page: 1,
      page_size: 10,
    });
  };

  const gotoPage = (page) => {
    setFilter({ ...filter, page: Math.max(1, Math.min(page, totalPages || 1)) });
  };

  const onDownloadSuccess = (message) => {
    setAlert({ show: true, message, type: "success" });
  };

  return (
    <MainLayout resource_key="schedule">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0 text-gray-800">KPI Instalasi</h1>
          </div>
          <div className="col-auto">
            {hasAccess("view-schedule-kpi") && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowModalDownload(true)}
              >
                <i className="fas fa-download" aria-hidden="true"></i> Unduh Data KPI Instalasi
              </button>
            )}
          </div>
        </div>

        <Box title={`Daftar KPI Instalasi (Total Data: ${meta.count})`} loading={loading}>
          <form onSubmit={applyFilter} className="mb-4">
            <div className="row align-items-end">
              <div className="col-12 col-md-4 col-lg-2">
                <div className="form-group">
                  <label htmlFor="installation-kpi-status">Status</label>
                  <select
                    id="installation-kpi-status"
                    className="form-control"
                    value={formFilter.survey_status}
                    onChange={(event) => changeFilter("survey_status", event.target.value)}
                  >
                    <option value="">SEMUA</option>
                    <option value="survey-uninstallable">Sudah Survey - Ditolak</option>
                    <option value="survey-canceled">Sudah Survey - Dibatalkan</option>
                    <option value="survey-unpaid">Sudah Survey - Belum Bayar</option>
                    <option value="survey-paid">Sudah Survey - Siap Pasang</option>
                    <option value="survey-installed">Sudah Survey - Sudah Dipasang</option>
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-4 col-lg-2">
                <div className="form-group">
                  <label htmlFor="installation-kpi-project">Project/Site</label>
                  <select
                    id="installation-kpi-project"
                    className="form-control"
                    value={formFilter.project_id}
                    onChange={(event) => changeFilter("project_id", event.target.value)}
                  >
                    <option value="">SEMUA</option>
                    {projects.map((project) => (
                      <option value={project.id} key={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-4 col-lg-2">
                <div className="form-group">
                  <label htmlFor="installation-kpi-date-from">Tanggal Dari</label>
                  <input
                    type="date"
                    id="installation-kpi-date-from"
                    className="form-control"
                    value={formFilter.date_from}
                    onChange={(event) => changeFilter("date_from", event.target.value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-4 col-lg-2">
                <div className="form-group">
                  <label htmlFor="installation-kpi-date-until">Tanggal Sampai</label>
                  <input
                    type="date"
                    id="installation-kpi-date-until"
                    className="form-control"
                    value={formFilter.date_until}
                    onChange={(event) => changeFilter("date_until", event.target.value)}
                  />
                </div>
              </div>
              <div className="col-auto">
                <div className="form-group">
                  <button type="submit" className="btn btn-primary">
                    Filter
                  </button>
                  <button type="button" className="btn btn-default" onClick={resetFilter}>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </form>

          <AlertBox {...alert} setAlert={setAlert} />
          {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

          <div className="table-responsive">
            <table className="table table-hover table-bordered table-striped">
              <thead>
                <tr>
                  <th scope="col">No. Registrasi</th>
                  <th scope="col">Nama Pelanggan</th>
                  <th scope="col">Project</th>
                  <th scope="col">No. Penugasan</th>
                  <th scope="col">Status</th>
                  <th scope="col">Tanggal Registrasi</th>
                  <th scope="col">Waktu Penugasan</th>
                  <th scope="col">Dijadwalkan Oleh</th>
                  <th scope="col">Waktu BAST</th>
                  <th scope="col">BAST Oleh</th>
                  <th scope="col">Durasi</th>
                </tr>
              </thead>
              <tbody>
                {!loading && !errorMsg && data.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center">
                      Tidak ada data
                    </td>
                  </tr>
                )}
                {data.map((item, index) => (
                  <tr key={index}>
                    <td>{item.registration_number}</td>
                    <td className="text-nowrap">{item.name}</td>
                    <td>{item.project}</td>
                    <td className="text-nowrap">{item.schedule_number}</td>
                    <td>
                      <InstallationStatusBadge status={item.status} />
                    </td>
                    <td className="text-nowrap">{formatDate(item.registered_at)}</td>
                    <td className="text-nowrap">{formatDate(item.assigned_at)}</td>
                    <td className="text-nowrap">{item.assigned_by || "-"}</td>
                    <td className="text-nowrap">{formatDate(item.bast_at)}</td>
                    <td className="text-nowrap">{item.bast_by || "-"}</td>
                    <td className="text-nowrap">{item.total_time || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && meta.count > meta.page_size && (
            <Pagination
              totalpage={totalPages}
              selectedpage={meta.page}
              clickpage={gotoPage}
              next={() => gotoPage(meta.page + 1)}
              prev={() => gotoPage(meta.page - 1)}
            />
          )}
        </Box>
      </div>
      <ModalDownloadInstallationKpi
        show={showModalDownload}
        onClose={() => setShowModalDownload(false)}
        onSuccess={onDownloadSuccess}
      />
    </MainLayout>
  );
}
